import { MessageWithActionAndData } from "@me8eon/sessions_messages";
import React from "react";
import { UserMessageComponent } from "../organisms/MessageItem/UserMessageComponent";
import { AssistantMessageComponent } from "../organisms/MessageItem/AssistantMessageComponent";
import { ToolMessageComponent } from "../organisms/MessageItem/ToolMessageComponent";
import { NexthinkRecommendationMessageComponent } from "../organisms/MessageItem/NexthinkRecommendationMessageComponent";

type MessageByRoleRecord = {
    [key in MessageWithActionAndData['role']]: React.FC<{
    message: MessageWithActionAndData
    isLast: boolean
    isStreaming?: boolean
}> | null
}

const MessageByRole: MessageByRoleRecord = {
    user: UserMessageComponent,
    assistant: AssistantMessageComponent,
    tool: ToolMessageComponent,
    system: null
};

// Sustainable IT widget is intentionally excluded: it is always displayed as a
// standalone widget (via ToolMessageComponent), never merged with answer text.
const NEXTHINK_WIDGET_NAMES = new Set(["nextthink_action", "nexthink_smartsolution"]);

const parseNexthinkWidgetValues = (message: MessageWithActionAndData): any | null => {
    if (message.role !== "tool" || typeof message.content !== "string") return null;
    try {
        const values = JSON.parse(message.content);
        const widgetName = values?._meta?.widget?.name;
        return widgetName && NEXTHINK_WIDGET_NAMES.has(widgetName) ? values : null;
    } catch {
        return null;
    }
};

const isAssistantTextMessage = (message: MessageWithActionAndData): boolean =>
    message.role === "assistant" && !message.toolCalls && typeof message.content === "string" && message.content.length > 0;

export const useMapMessagesToComponentsHook = (messages: MessageWithActionAndData[]) => {
    const consumedIndices = new Set<number>();

    return messages.map((m, i) => {
        if (consumedIndices.has(i)) return null;

        const nexthinkWidgetValues = parseNexthinkWidgetValues(m);
        if (nexthinkWidgetValues) {
            // Merge this Nexthink recommendation with the assistant text that follows it
            // (within the same turn, i.e. before the next user message) into a single response.
            let textMessage: MessageWithActionAndData | null = null;
            let textIndex = -1;
            for (let j = i + 1; j < messages.length; j++) {
                const next = messages[j];
                if (next.role === "user") break;
                if (isAssistantTextMessage(next)) {
                    textMessage = next;
                    textIndex = j;
                    break;
                }
            }
            if (textIndex >= 0) consumedIndices.add(textIndex);

            const lastRenderedIndex = textIndex >= 0 ? textIndex : i;
            const isStreaming = Boolean((messages[lastRenderedIndex].data as any)?.streaming);

            return (
                <NexthinkRecommendationMessageComponent
                    key={`message_${i}`}
                    widgetValues={nexthinkWidgetValues}
                    textMessage={textMessage}
                    isLast={lastRenderedIndex === messages.length - 1}
                    isStreaming={isStreaming}
                />
            );
        }

        const MessageComponent = MessageByRole[m.role];
        if (!MessageComponent) return null;

        const isStreaming = Boolean((m.data as any)?.streaming);

        return (
            <MessageComponent
                key={`message_${i}`}
                message={m}
                isLast={i === messages.length - 1}
                isStreaming={isStreaming}
            />
        );
    }).filter(Boolean);
};
